import { findKeyInObj } from '../../utils/objects';
import {
  BRAND_KEY,
  LOCALE_KEY,
  THEME_KEY,
  UIBASE_KEY,
} from './watson-config-action';
import { isStringDebugEnabled } from '../redux-dev/dev-selectors';
import { isTeacher } from 'app/api/redux-bridge/session/selectors';
import {
  getBookshelfReaderRedirectUrl,
  getInternalReaderUrl,
} from 'app/ui/pages/shared/url-builder';
import { replaceParams } from 'app/utils/strings';
import { isDevOrLocal } from 'app/utils/env';
import { POSSIBLE_SWIMLANES } from 'app/api/redux-bridge/assetGroups/recommendations/utils';

const emptyArray = [];

export function getString(state, id) {
  let value = findKeyInObj(state.config[LOCALE_KEY].data, id);
  if (value && isStringDebugEnabled(state)) {
    value = '😱'.repeat(value.length);
  }
  return value;
}

export function getStringWithParams(state, id, params) {
  return replaceParams(getString(state, id), params);
}

export function getTheme(state) {
  return state.config[THEME_KEY].data;
}

export function getBrand(state) {
  return state.config[BRAND_KEY].data || {};
}

export function getLocale(state) {
  return state.config[LOCALE_KEY].data || {};
}

export function hasBrandError(state) {
  return state.config[BRAND_KEY].hasError;
}

export function getBrandCommon(state) {
  return getBrand(state).common || {};
}

export function getBrandId(state) {
  return state.config[BRAND_KEY].successQuery;
}

export function getComputedBridgeBrand(state) {
  // If you're passing ?brand= to test in dev, you probably want segmentation turned off
  if (getBrandId(state) !== window.location.hostname && isDevOrLocal()) {
    return null;
  }

  const common = getBrandCommon(state);
  if (common.bridgeBrand) {
    return common.bridgeBrand;
  }
  return null;
}

export function getStudentBrandName(state) {
  return getBrandCommon(state).name;
}

export function getTeacherBrandName(state) {
  return getBrand(state).teacher.name;
}

export function getLocaleId(state) {
  return state.config[LOCALE_KEY].successQuery;
}

export function getThemeId(state) {
  return state.config[THEME_KEY].successQuery;
}

function isItemReady(state, key) {
  const node = state.config[key];
  return !!node.data;
}

function isItemLoaded(state, key) {
  const node = state.config[key];
  return !!node.data || node.hasError;
}

export function isConfigOk(state) {
  const itemsToCheck = [BRAND_KEY, THEME_KEY, LOCALE_KEY];
  return itemsToCheck.reduce((acc, cv) => {
    return acc && isItemReady(state, cv);
  }, true);
}

export function isConfigLoaded(state) {
  const itemsToCheck = [BRAND_KEY, THEME_KEY, LOCALE_KEY];
  return itemsToCheck.reduce((acc, cv) => {
    return acc && isItemLoaded(state, cv);
  }, true);
}

export function getUiBase(state) {
  return state.config[UIBASE_KEY];
}

export function getBrandVal(state, key) {
  const data = getBrand(state);
  return findKeyInObj(data, key);
}

export function getQueryParamsForReload(state) {
  const currentBrand = getBrandId(state);
  const currentLocale = getLocaleId(state);
  const currentTheme = getThemeId(state);
  const brandCommon = getBrandCommon(state);

  const params = {};

  if (currentBrand !== window.location.hostname) {
    params.brand = currentBrand;
  }
  if (currentLocale !== brandCommon.locale) {
    params.locale = currentLocale;
  }
  if (currentTheme !== brandCommon.theme) {
    params.theme = currentTheme;
  }
  return params;
}

export function isJigsawOnly(state) {
  const base = getBrandCommon(state).jigsawOnly;
  return base && base.enabled;
}

function getMulliganHost(state) {
  return 'https://' + getBrandCommon(state).mulliganHost;
}

export function getDevicesLink(state) {
  return `${getMulliganHost(state)}/devices`;
}

export function getAccountCenterLink(state) {
  return `${getMulliganHost(state)}/user/profile`;
}

export function isTeacherPortalEnabled(state) {
  return !isJigsawOnly(state) && getBrandCommon(state).teacherPortalEnabled;
}

function getTeacherTaxonomyBase(state) {
  return getBrand(state).teacher.taxonomies;
}

const FILTER = 'filter';
const BROWSE = 'browse';

function selectTaxonomies(state, key) {
  const base = getTeacherTaxonomyBase(state);
  if (base && base[key].enabled) {
    return base[key].taxonomies || emptyArray;
  }
  return emptyArray;
}

export function getTeacherTaxonomyBrowseEnabled(state) {
  const base = getTeacherTaxonomyBase(state);
  return base && base[BROWSE].enabled;
}

export function getTeacherTaxonomyFilters(state) {
  return selectTaxonomies(state, FILTER);
}

export function getTeacherTaxonomyBrowse(state) {
  return selectTaxonomies(state, BROWSE);
}

export function getJigsawHost(state) {
  return getBrandCommon(state).jigsawHost;
}

function getValueForCurrentUiBase(state, featurePath, defaultReturn) {
  const uiBase = getUiBase(state);
  const configBase = getBrand(state)[uiBase];
  const item = findKeyInObj(configBase, featurePath);
  if (item === null) {
    return defaultReturn;
  }
  return item;
}

export function thumbnailsEnabled(state) {
  return getValueForCurrentUiBase(state, 'thumbnails.enabled', true);
}

export function onboardingEnabled(state) {
  return getValueForCurrentUiBase(state, 'onboarding.enabled', true);
}

export function getReaderUrl(state, vbid) {
  if (
    isTeacher(state) &&
    getBrandVal(state, 'teacher.readerRedirect.enabled')
  ) {
    const url = getBrandVal(state, 'teacher.readerRedirect.bookshelfUrl');
    return getBookshelfReaderRedirectUrl(url, vbid);
  }
  return getInternalReaderUrl(vbid);
}

export function getLoginRedirect(state) {
  if (getBrandVal(state, 'common.loginRedirect.enabled')) {
    return getBrandVal(state, 'common.loginRedirect.url');
  }
  return null;
}

export function getBrandItem(state, key) {
  return findKeyInObj(getBrand(state), key);
}

export function getTeacherExploreSwimlanes(state) {
  return POSSIBLE_SWIMLANES.reduce((acc, cv) => {
    const dataObj = getBrandItem(state, `teacher.exploreSwimlanes.${cv}`);
    if (dataObj && dataObj.enabled) {
      acc.push(cv);
    }
    return acc;
  }, []).filter(val => !!val);
}

export function isFeenyEnabled(state) {
  return getBrandItem(state, 'teacher.feeny.enabled');
}

export function getAssignmentCreatorUrlBase(state) {
  return getBrandItem(state, 'teacher.feeny.creatorUrl');
}

export function ancillariesEnabled(state) {
  return getValueForCurrentUiBase(state, 'ancillaries.enabled', true);
}

export function getBookshelfUrl(state) {
  return getBrandItem(state, 'common.bookshelfUrl');
}



// WEBPACK FOOTER //
// ./src/app/api/redux-watson/watson-config-selectors.js